import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { getBattleStakeSettings, updateBattleStakeSettings } from '../Api';
import BattleStakeSettingsDto from '../models/dtos/BattleStakeSettingsDto';

function parseLocaleInt(raw: string): number {
    const t = raw.trim().replace(/\s/g, '');
    if (t.includes(',') || t.includes('.')) return Number.NaN;
    return parseInt(t, 10);
}

export default function BattleStakeSettingsPage() {
    const [loading, setLoading] = React.useState(true);
    const [minStake, setMinStake] = React.useState<string>('500');
    const [maxStake, setMaxStake] = React.useState<string>('1000000');
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogTitle, setDialogTitle] = React.useState('');
    const [dialogMsg, setDialogMsg] = React.useState('');

    const applyDto = (dto: BattleStakeSettingsDto | null) => {
        if (dto != null) {
            setMinStake(String(dto.minStakeBetcoins ?? 500));
            const max = dto.maxStakeBetcoins ?? 0;
            setMaxStake(max > 0 ? String(max) : '');
        }
    };

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const dto = await getBattleStakeSettings();
            if (!cancelled) {
                applyDto(dto);
                setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const openDialog = (title: string, message: string) => {
        setDialogTitle(title);
        setDialogMsg(message);
        setDialogOpen(true);
    };

    const onSave = async () => {
        const minS = parseLocaleInt(minStake);
        const maxRaw = maxStake.trim() === '' ? 0 : parseLocaleInt(maxStake);
        if (Number.isNaN(minS) || Number.isNaN(maxRaw)) {
            openDialog('Error', 'Enter whole numbers (leave max empty or 0 for unlimited).');
            return;
        }
        if (minS < 1) {
            openDialog('Error', 'Minimum stake must be at least 1.');
            return;
        }
        if (maxRaw < 0) {
            openDialog('Error', 'Maximum stake cannot be negative (use empty or 0 for unlimited).');
            return;
        }
        if (maxRaw > 0 && minS > maxRaw) {
            openDialog('Error', 'Minimum stake cannot exceed maximum stake.');
            return;
        }
        const payload: BattleStakeSettingsDto = {
            minStakeBetcoins: minS,
            maxStakeBetcoins: maxRaw,
        };
        const ok = await updateBattleStakeSettings(payload);
        if (!ok) {
            openDialog('Error', 'Failed to save. Check server logs and API configuration.');
            return;
        }
        const dto = await getBattleStakeSettings();
        applyDto(dto);
        openDialog(
            'Saved',
            maxRaw > 0
                ? `Saved. Server enforces min ${minS} / max ${maxRaw} Betcoins for all platforms.`
                : `Saved. Server enforces min ${minS} Betcoins; max is unlimited.`,
        );
    };

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Typography variant="h4">Battle stake limits</Typography>

            <Alert severity="info">
                <Typography variant="subtitle2" gutterBottom>What these fields do</Typography>
                <Typography variant="body2" component="div">
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        <li>
                            <strong>Minimum (required)</strong> — lowest Betcoins allowed when creating a battle.
                            Default <code>500</code> (same as before).
                        </li>
                        <li>
                            <strong>Maximum (optional)</strong> — leave empty or <code>0</code> for no ceiling.
                            Only set a number if you want a hard cap. Seeded default is <code>1_000_000</code>.
                        </li>
                        <li>
                            Challenge <strong>fee %</strong> is still Firebase Remote Config key <code>challengeFee</code>, not here.
                        </li>
                    </ul>
                </Typography>
            </Alert>

            <Alert severity="warning">
                <Typography variant="subtitle2" gutterBottom>Clients &amp; compatibility</Typography>
                <Typography variant="body2" component="div">
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        <li>
                            <strong>Server</strong> always enforces these limits on create (all platforms, including iOS and old apps).
                        </li>
                        <li>
                            <strong>New Android</strong> reads <code>GET /api/v7/me/challenge-limits</code> and applies min/max in Quick Bet Battle + create/accept battle UI.
                        </li>
                        <li>
                            <strong>iOS / older Android</strong> still show a hardcoded UI floor of ~500. They are not broken by this page:
                            keeping min at <code>500</code> (or lower) is safe. Raising min above 500 can make old clients
                            submit stakes the server rejects until those apps update.
                        </li>
                        <li>
                            Keys in DB: <code>battle.min_stake_betcoins</code>, <code>battle.max_stake_betcoins</code> (0 = unlimited).
                        </li>
                    </ul>
                </Typography>
            </Alert>

            {loading ? (
                <Box><CircularProgress /></Box>
            ) : (
                <Grid container spacing={2} maxWidth="sm">
                    <Grid item xs={12}>
                        <TextField
                            label="Minimum battle stake (betcoins) — required"
                            type="number"
                            fullWidth
                            required
                            value={minStake}
                            onChange={(e) => setMinStake(e.target.value)}
                            inputProps={{ min: 1 }}
                            helperText="Recommended: keep 500 unless new Android is already in production"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Maximum battle stake — optional"
                            type="number"
                            fullWidth
                            value={maxStake}
                            onChange={(e) => setMaxStake(e.target.value)}
                            inputProps={{ min: 0 }}
                            helperText="Empty or 0 = unlimited (no max). Balance still limits what a user can stake."
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" onClick={onSave}>Save</Button>
                    </Grid>
                </Grid>
            )}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent><Typography>{dialogMsg}</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>OK</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
